import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Trans } from "react-i18next";
import { Flex } from "../../../../base/components/Flex";
import { useTranslationPrefix } from "../../../../hooks/useTranslationPrefix";
import InfoTooltip from "../components/InfoTooltip";
import QrButton from "../components/QrButton";
import QrPageLogo from "../components/QrPageLogo";

interface Props {
  onYesClick: () => void;
  onNoClick: () => void;
}

const QrHealthAndSafetuStep = ({ onYesClick, onNoClick }: Props) => {
  const { _t } = useTranslationPrefix("LaunchPlaylistQrPageNew");
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState(false);

  return (
    <Flex
      flexDirection="column"
      width="100%"
      minHeight="100%"
      alignItems="center"
      gap={2}
      px={{ xs: 2, md: 6 }}
    >
      <Flex
        flexDirection="column"
        width="100%"
        alignItems={{ xs: "center", md: "end" }}
      >
        <QrPageLogo />
      </Flex>
      <Flex
        sx={{
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-evenly",
          height: "100%",
          gap: 2,
        }}
      >
        <Box></Box>
        <Typography variant="h2" fontSize="1.75rem" textAlign="center">
          {_t(
            "safetyTitle",
            "Please confirm that using virtual reality is safe for you."
          )}
        </Typography>

        <Flex
          sx={{
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            maxWidth: "64ch",
          }}
        >
          <Typography textAlign="center" fontWeight={600}>
            {_t(
              "safetyDeclaration",
              "Declaration of Medical Fitness and Personal Responsibility for Using VR"
            )}
          </Typography>
          <Typography textAlign="center">
            {_t(
              "safetyDeclarationProclamation",
              "I declare that I am using virtual reality voluntarily and that I have no health conditions that would prevent its safe use (e.g., epilepsy, mental illness, pregnancy, etc."
            )}
          </Typography>
        </Flex>
        <Flex flexDirection="column">
          <InfoTooltip
            title={
              <Flex
                flexDirection="column"
                gap={1}
                maxHeight="60vh"
                overflow="auto"
              >
                <Typography whiteSpace="pre-wrap">
                  <Trans
                    i18nKey="LaunchPlaylistQrPageNew.safetyDeclarationDetail"
                    defaults={`The use of VR is <0>not recommended for people:</0>
with epilepsy or photosensitive epilepsy,
with serious heart or blood vessel diseases,
with balance disorders, frequent dizziness or migraines,
with mental illnesses that could be aggravated by VR stimulation,
pregnant women,
children under 13 years of age.
When using VR, temporary nausea, dizziness, headaches, blurred vision or disorientation may occur. If you feel unwell, stop the activity immediately.`}
                  >
                    <strong></strong>
                  </Trans>
                </Typography>
              </Flex>
            }
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <InfoIcon
                color="primary"
                sx={{
                  fontSize: "1.45rem",
                  ml: "-2px",
                  mr: "9px",
                }}
              />
              <Typography fontWeight={600}>
                {_t(
                  "safetyShowMore",
                  "More about contraindications for using virtual reality"
                )}
              </Typography>
            </Box>
          </InfoTooltip>
          <FormControl error={error} component="fieldset">
            <FormControlLabel
              checked={checked}
              onChange={(_, checked) => {
                setChecked(checked);
                if (checked) {
                  setError(false);
                }
              }}
              control={<Checkbox sx={error ? { color: "red" } : {}} />}
              label={
                <Typography fontWeight={600}>
                  {_t("safetyCheckboxLabel", "Confirm")}
                </Typography>
              }
            />

            {error && (
              <FormHelperText
                error={true}
                sx={{
                  position: "initial",
                  transform: "none",
                  mt: "-0.5rem",
                  ml: "2rem",
                }}
              >
                {_t("safetyConfirmRequired", "Required to continue")}
              </FormHelperText>
            )}
          </FormControl>
        </Flex>
        <Flex
          sx={{
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <QrButton
            id="safety-confirm"
            color="primary"
            onClick={() => {
              if (!checked) {
                setError(true);
                return;
              }
              onYesClick();
            }}
          >
            {_t("safetyContinue", "Continue")}
          </QrButton>
          <Button
            id="safety-back"
            color="primary"
            variant="text"
            sx={{ mx: "auto" }}
            onClick={onNoClick}
          >
            {_t("safetyBack", "Back")}
          </Button>
        </Flex>
      </Flex>
      <Box height="3rem"></Box>
    </Flex>
  );
};

export default QrHealthAndSafetuStep;
